import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Button,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  CssBaseline,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  TrendingUp as TrendingIcon,
  Business as BusinessIcon,
  QuestionAnswer as QuestionIcon,
  PlayArrow as PlayIcon,
  Work as WorkIcon,
  Add as AddIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../../components/top_bar/TopBar';
import { Helmet } from 'react-helmet';
import { useUser } from '@clerk/clerk-react';
import { GetUser } from '../../components/auth/GetUser';
import './EnhancedQuestionBank.css';

const EnhancedQuestionBank = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSignedIn, user, isLoaded } = useUser();

  // Company logo mapping
  const getCompanyLogo = (companyName) => {
    const logos = {
      // Tech Companies
      'Meta': 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/2560px-Meta_Platforms_Inc._logo.svg.png',
      'Google': 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/2560px-Google_2015_logo.svg.png',
      'Apple': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/488px-Apple_logo_black.svg.png',
      'Microsoft': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/Microsoft_logo_%282012%29.svg/2560px-Microsoft_logo_%282012%29.svg.png',
      'Amazon': 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/2560px-Amazon_logo.svg.png',
      'Amazon Web Services': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Amazon_Web_Services_Logo.svg/2560px-Amazon_Web_Services_Logo.svg.png',
      'Netflix': 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Netflix_2015_logo.svg/2560px-Netflix_2015_logo.svg.png',
      'Tesla': 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/bb/Tesla_T_symbol.svg/1024px-Tesla_T_symbol.svg.png',
      'Uber': 'https://upload.wikimedia.org/wikipedia/commons/c/cc/Uber_logo_2018.png',
      'DoorDash': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fe/DoorDash_logo.svg/2560px-DoorDash_logo.svg.png',
      'Airbnb': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/69/Airbnb_Logo_Bélo.svg/2560px-Airbnb_Logo_Bélo.svg.png',
      'LinkedIn': 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/LinkedIn_logo_initials.png/600px-LinkedIn_logo_initials.png',
      'Twitter': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Logo_of_Twitter.svg/512px-Logo_of_Twitter.svg.png',
      'X': 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5a/X_icon_2.svg/512px-X_icon_2.svg.png',
      'TikTok': 'https://upload.wikimedia.org/wikipedia/en/thumb/a/a9/TikTok_logo.svg/1024px-TikTok_logo.svg.png',
      'Spotify': 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Spotify_logo_without_text.svg/512px-Spotify_logo_without_text.svg.png',
      'Stripe': 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/2560px-Stripe_Logo%2C_revised_2016.svg.png',
      'Salesforce': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Salesforce.com_logo.svg/2560px-Salesforce.com_logo.svg.png',
      'HubSpot': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3f/HubSpot_Logo.svg/2560px-HubSpot_Logo.svg.png',
      'Oracle': 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/Oracle_logo.svg/2560px-Oracle_logo.svg.png',
      
      // Consulting Companies  
      'McKinsey & Company': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/McKinsey_and_Company_Logo_1.svg/2560px-McKinsey_and_Company_Logo_1.svg.png',
      'Boston Consulting Group': 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Boston_Consulting_Group_2020_logo.svg/2560px-Boston_Consulting_Group_2020_logo.svg.png',
      'Bain & Company': 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/49/Bain_and_Company_logo.svg/2560px-Bain_and_Company_logo.svg.png',
      'Deloitte': 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Deloitte.svg/2560px-Deloitte.svg.png',
      'PwC': 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/05/PwC_Logo.svg/2560px-PwC_Logo.svg.png',
      
      // Finance Companies
      'Goldman Sachs': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/61/Goldman_Sachs.svg/2560px-Goldman_Sachs.svg.png',
      'JPMorgan Chase': 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/JPMorgan_Chase_logo.svg/2560px-JPMorgan_Chase_logo.svg.png',
      'Morgan Stanley': 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/Morgan_Stanley_Logo_1.svg/2560px-Morgan_Stanley_Logo_1.svg.png',
      'BlackRock': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/BlackRock_Logo.svg/2560px-BlackRock_Logo.svg.png',
      'Citadel': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Citadel_LLC_logo.svg/2560px-Citadel_LLC_logo.svg.png',
      'Credit Suisse': 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/Credit_Suisse_Logo.svg/2560px-Credit_Suisse_Logo.svg.png',
      'Fidelity': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6b/Fidelity_Investments_logo.svg/2560px-Fidelity_Investments_logo.svg.png',
      
      // Other Companies
      'Walmart': 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/Walmart_logo.svg/2560px-Walmart_logo.svg.png',
      'Disney': 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Disney%2B_logo.svg/2560px-Disney%2B_logo.svg.png',
      'Capital One': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/98/Capital_One_logo.svg/2560px-Capital_One_logo.svg.png'
    };
    return logos[companyName] || null;
  };
  const TITLE = 'Real Question Bank - Mirwork';

  // State management
  const [questions, setQuestions] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userVotes, setUserVotes] = useState({}); // Track user votes {questionId: 'upvote'|'downvote'}
  const [isPremium, setIsPremium] = useState(false); // Track user premium status

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterQuestionType, setFilterQuestionType] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterInterviewType, setFilterInterviewType] = useState('');
  
  // New multi-industry filters
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterJobFamily, setFilterJobFamily] = useState('');
  const [filterQuestionCategory, setFilterQuestionCategory] = useState('');

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalQuestions: 0,
    limit: 12
  });

  // UI state
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch data functions
  const fetchQuestions = async (page = 1) => {
    return fetchQuestionsWithFilters(page);
  };

  const fetchQuestionsWithFilters = async (page = 1, overrideFilters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });

      // Use override filters if provided, otherwise use state values
      const currentSearch = overrideFilters.search !== undefined ? overrideFilters.search : searchTerm;
      const currentCompany = overrideFilters.company !== undefined ? overrideFilters.company : filterCompany;
      const currentQuestionType = overrideFilters.questionType !== undefined ? overrideFilters.questionType : filterQuestionType;
      const currentDifficulty = overrideFilters.difficulty !== undefined ? overrideFilters.difficulty : filterDifficulty;
      const currentInterviewType = overrideFilters.interviewType !== undefined ? overrideFilters.interviewType : filterInterviewType;
      
      // New multi-industry filters
      const currentIndustry = overrideFilters.industry !== undefined ? overrideFilters.industry : filterIndustry;
      const currentJobFamily = overrideFilters.jobFamily !== undefined ? overrideFilters.jobFamily : filterJobFamily;
      const currentQuestionCategory = overrideFilters.questionCategory !== undefined ? overrideFilters.questionCategory : filterQuestionCategory;

      if (currentSearch) params.append('search', currentSearch);
      if (currentCompany) params.append('company', currentCompany);
      if (currentQuestionType) params.append('standardizedQuestionType', currentQuestionType);
      if (currentDifficulty) params.append('difficulty', currentDifficulty);
      if (currentInterviewType) params.append('interviewType', currentInterviewType);
      
      // Add new filter parameters
      if (currentIndustry) params.append('industry', currentIndustry);
      if (currentJobFamily) params.append('jobFamily', currentJobFamily);
      if (currentQuestionCategory) params.append('questionCategory', currentQuestionCategory);


      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/questions?${params}`);
      
      if (response.data.success) {
        setQuestions(response.data.data.questions);
        setPagination(response.data.data.pagination);
      }
    } catch (err) {
      setError('Failed to fetch questions');
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/filters`);
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchDynamicFilterOptions = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to API call (only non-empty values)
      if (filterIndustry) params.append('industry', filterIndustry);
      if (filterJobFamily) params.append('jobFamily', filterJobFamily);
      if (filterCompany) params.append('company', filterCompany);
      if (filterQuestionCategory) params.append('questionCategory', filterQuestionCategory);
      if (filterDifficulty) params.append('difficulty', filterDifficulty);
      if (filterInterviewType) params.append('interviewType', filterInterviewType);
      if (filterQuestionType) params.append('questionType', filterQuestionType);

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/dynamic-filters?${params}`);
      if (response.data.success) {
        setFilterOptions(response.data.data.availableFilters);
      }
    } catch (err) {
      console.error('Error fetching dynamic filter options:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/stats`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchUserVotes = async () => {
    try {
      let params = {};
      if (isSignedIn && user) {
        params.userEmail = user.primaryEmailAddress.emailAddress;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/user-votes`, { params });
      if (response.data.success) {
        setUserVotes(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching user votes:', err);
    }
  };

  // Event handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Generic filter change handler with dynamic filter updates
  const handleFilterChange = async (filterType, value) => {
    // Update the specific filter state
    switch (filterType) {
      case 'industry':
        setFilterIndustry(value);
        break;
      case 'jobFamily':
        setFilterJobFamily(value);
        break;
      case 'company':
        setFilterCompany(value);
        break;
      case 'questionCategory':
        setFilterQuestionCategory(value);
        break;
      case 'difficulty':
        setFilterDifficulty(value);
        break;
      case 'questionType':
        setFilterQuestionType(value);
        break;
      case 'interviewType':
        setFilterInterviewType(value);
        break;
      default:
        break;
    }

    // Update dynamic filter options and search
    setTimeout(async () => {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      
      // Build filter override object with new value
      const filterOverride = { [filterType]: value };
      
      // Create temporary filter state with the new value for dynamic filters call
      const tempFilterState = {
        industry: filterType === 'industry' ? value : filterIndustry,
        jobFamily: filterType === 'jobFamily' ? value : filterJobFamily,
        company: filterType === 'company' ? value : filterCompany,
        questionCategory: filterType === 'questionCategory' ? value : filterQuestionCategory,
        difficulty: filterType === 'difficulty' ? value : filterDifficulty,
        interviewType: filterType === 'interviewType' ? value : filterInterviewType,
        questionType: filterType === 'questionType' ? value : filterQuestionType,
      };
      
      // Fetch updated questions
      await fetchQuestionsWithFilters(1, filterOverride);
      
      // Fetch updated dynamic filter options with new state
      await fetchDynamicFilterOptionsWithState(tempFilterState);
    }, 100);
  };

  // Helper function to fetch dynamic filters with specific state
  const fetchDynamicFilterOptionsWithState = async (tempState) => {
    try {
      const params = new URLSearchParams();
      
      // Add filters to API call (only non-empty values)
      if (tempState.industry) params.append('industry', tempState.industry);
      if (tempState.jobFamily) params.append('jobFamily', tempState.jobFamily);
      if (tempState.company) params.append('company', tempState.company);
      if (tempState.questionCategory) params.append('questionCategory', tempState.questionCategory);
      if (tempState.difficulty) params.append('difficulty', tempState.difficulty);
      if (tempState.interviewType) params.append('interviewType', tempState.interviewType);
      if (tempState.questionType) params.append('questionType', tempState.questionType);

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/dynamic-filters?${params}`);
      if (response.data.success) {
        setFilterOptions(response.data.data.availableFilters);
      }
    } catch (err) {
      console.error('Error fetching dynamic filter options with state:', err);
    }
  };

  // Legacy handlers for backward compatibility
  const handleCompanyFilterChange = (event) => {
    handleFilterChange('company', event.target.value);
  };

  const handleQuestionTypeFilterChange = (event) => {
    handleFilterChange('questionType', event.target.value);
  };

  const handleDifficultyFilterChange = (event) => {
    handleFilterChange('difficulty', event.target.value);
  };

  const handleInterviewTypeFilterChange = (event) => {
    handleFilterChange('interviewType', event.target.value);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterCompany('');
    setFilterQuestionType('');
    setFilterDifficulty('');
    setFilterInterviewType('');
    setFilterIndustry('');
    setFilterJobFamily('');
    setFilterQuestionCategory('');
    // Auto search after clearing filters and reset to original filter options
    setTimeout(async () => {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      await fetchQuestions(1);
      await fetchFilterOptions(); // Reset to original filter options
    }, 100);
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchQuestions(1);
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchQuestions(page);
  };

  const handleQuestionClick = async (question) => {
    setSelectedQuestion(question);
    setDialogOpen(true);
    
    // Track question view
    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/questions/${question._id}`);
    } catch (err) {
      console.error('Error tracking question view:', err);
    }
  };

  const handlePracticeQuestion = (question) => {
    // Navigate to interview page with selected question (keep original logic)
    const params = new URLSearchParams({
      mode: 'practice',
      questionId: question._id,
      company: question.company,
      type: question.standardizedQuestionType
    });
    navigate(`/interview?${params.toString()}`);
  };

  const handleVote = async (questionId, action) => {
    // Check if user is signed in
    if (!isSignedIn || !user) {
      alert('Please sign in to vote on questions!');
      return;
    }

    // Check if user has already voted on this question
    if (userVotes[questionId]) {
      alert('You have already voted on this question!');
      return;
    }


    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/questionBank/questions/${questionId}/rate`, {
        action,
        userEmail: user.primaryEmailAddress.emailAddress
      });
      
      if (response.data.success) {
        // Update local state to reflect the vote
        setUserVotes(prev => ({
          ...prev,
          [questionId]: action
        }));

        // Update the specific question's vote count locally
        setQuestions(prevQuestions => 
          prevQuestions.map(question => 
            question._id === questionId 
              ? {
                  ...question,
                  upvotes: response.data.data.upvotes,
                  downvotes: response.data.data.downvotes
                }
              : question
          )
        );
      }
    } catch (err) {
      console.error('Error voting:', err);
      if (err.response && err.response.data && err.response.data.error) {
        alert(err.response.data.error);
      } else {
        alert('Failed to submit vote. Please try again.');
      }
    }
  };

  // Effects
  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        fetchFilterOptions(),
        fetchStats()
      ]);
      await fetchQuestions(1);
    };
    
    initializeData();
  }, []);

  // Fetch user votes when user authentication status changes
  useEffect(() => {
    if (isLoaded) {
      fetchUserVotes();
    }
  }, [isLoaded, isSignedIn, user]);

  // Fetch user premium status
  useEffect(() => {
    const fetchUserData = async () => {
      if (isLoaded) {
        const curUser = await GetUser(isSignedIn, user);
        setIsPremium(curUser?.premium || false);
      }
    };

    fetchUserData();
  }, [isLoaded, isSignedIn, user]);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return { backgroundColor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main };
      case 'Medium': return { backgroundColor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main };
      case 'Hard': return { backgroundColor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main };
      default: return { backgroundColor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main };
    }
  };

  if (loading && questions.length === 0) {
    return (
      <>
        <Helmet>
          <title>{TITLE}</title>
        </Helmet>
        <TopBar />
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <TopBar />
      <CssBaseline />
      <Container maxWidth="lg" sx={{ bgcolor: "#f5f5f5" }}>
        <Box sx={{ py: 4 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Real Interview Question Bank
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Practice with {stats.overview?.totalQuestions || 152} real questions from {stats.overview?.companiesCount || 14} top companies
            </Typography>
          </Box>

          {/* Search and Filter Section */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Grid container spacing={2}>
              {/* Search Bar - Full Width */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  placeholder="Search for questions, companies, or types..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              {/* First Row of Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="industry-filter-label">Industry</InputLabel>
                  <Select
                    labelId="industry-filter-label"
                    value={filterIndustry}
                    onChange={(e) => handleFilterChange('industry', e.target.value)}
                    label="Industry"
                  >
                    <MenuItem value="">All Industries</MenuItem>
                    {filterOptions.industries?.map((industry) => (
                      <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="job-family-filter-label">Job Family</InputLabel>
                  <Select
                    labelId="job-family-filter-label"
                    value={filterJobFamily}
                    onChange={(e) => handleFilterChange('jobFamily', e.target.value)}
                    label="Job Family"
                  >
                    <MenuItem value="">All Job Families</MenuItem>
                    {filterOptions.jobFamilies?.map((family) => (
                      <MenuItem key={family} value={family}>{family}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="company-filter-label">Company</InputLabel>
                  <Select
                    labelId="company-filter-label"
                    value={filterCompany}
                    onChange={handleCompanyFilterChange}
                    label="Company"
                  >
                    <MenuItem value="">All Companies</MenuItem>
                    {filterOptions.companies?.map((company) => (
                      <MenuItem key={company} value={company}>{company}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="difficulty-filter-label">Difficulty</InputLabel>
                  <Select
                    labelId="difficulty-filter-label"
                    value={filterDifficulty}
                    onChange={handleDifficultyFilterChange}
                    label="Difficulty"
                  >
                    <MenuItem value="">All Difficulties</MenuItem>
                    {filterOptions.difficulties?.map((diff) => (
                      <MenuItem key={diff} value={diff}>{diff}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Second Row of Filters */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="question-category-filter-label">Question Category</InputLabel>
                  <Select
                    labelId="question-category-filter-label"
                    value={filterQuestionCategory}
                    onChange={(e) => handleFilterChange('questionCategory', e.target.value)}
                    label="Question Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {filterOptions.questionCategories?.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="type-filter-label">Legacy Question Type</InputLabel>
                  <Select
                    labelId="type-filter-label"
                    value={filterQuestionType}
                    onChange={handleQuestionTypeFilterChange}
                    label="Legacy Question Type"
                  >
                    <MenuItem value="">All Legacy Types</MenuItem>
                    {filterOptions.standardizedTypes?.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Filter Tags Display */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                <Typography variant="body2" color="text.secondary">
                  Filters:
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, ml: 1, flexWrap: 'wrap' }}>
                  {filterCompany && (
                    <Chip 
                      label={`Company: ${filterCompany}`} 
                      onDelete={() => setFilterCompany('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
                    />
                  )}
                  {filterQuestionType && (
                    <Chip 
                      label={`Type: ${filterQuestionType}`} 
                      onDelete={() => setFilterQuestionType('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.1) }}
                    />
                  )}
                  {filterDifficulty && (
                    <Chip 
                      label={`Difficulty: ${filterDifficulty}`} 
                      onDelete={() => setFilterDifficulty('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.1) }}
                    />
                  )}
                  {searchTerm && (
                    <Chip 
                      label={`Search: ${searchTerm}`} 
                      onDelete={() => setSearchTerm('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}
                    />
                  )}
                  {filterIndustry && (
                    <Chip 
                      label={`Industry: ${filterIndustry}`} 
                      onDelete={() => setFilterIndustry('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.success.main, 0.1) }}
                    />
                  )}
                  {filterJobFamily && (
                    <Chip 
                      label={`Job Family: ${filterJobFamily}`} 
                      onDelete={() => setFilterJobFamily('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.1) }}
                    />
                  )}
                  {filterQuestionCategory && (
                    <Chip 
                      label={`Category: ${filterQuestionCategory}`} 
                      onDelete={() => setFilterQuestionCategory('')}
                      size="small"
                      sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.1) }}
                    />
                  )}
                  {(filterCompany || filterQuestionType || filterDifficulty || searchTerm || filterIndustry || filterJobFamily || filterQuestionCategory) && (
                    <Button size="small" onClick={resetFilters} sx={{ ml: 1 }}>
                      Clear All
                    </Button>
                  )}
                </Box>
              </Box>
              
              <Button 
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => window.open('https://docs.google.com/forms/d/e/1FAIpQLScYhxPJfPFI82eib6q7ta-8dcJLkDTO35xFISIPZAfRu3h1QQ/viewform?usp=header', '_blank')}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                Add Question
              </Button>
            </Box>
          </Paper>

          {/* Results Count */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Showing {questions.length} of {pagination.totalQuestions} results
            </Typography>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          {/* Questions List */}
          {questions.length > 0 ? (
            questions.map((question) => (
              <Card 
                key={question._id}
                className="question-card" 
                sx={{ 
                  mb: 3, 
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleQuestionClick(question)}
              >
                <CardContent>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {getCompanyLogo(question.company) ? (
                      <Box
                        component="img"
                        src={getCompanyLogo(question.company)}
                        alt={`${question.company} logo`}
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1,
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          // Fallback to BusinessIcon if logo fails to load
                          e.target.style.display = 'none';
                          const fallbackIcon = e.target.nextSibling;
                          if (fallbackIcon) fallbackIcon.style.display = 'inline';
                        }}
                      />
                    ) : null}
                    {!getCompanyLogo(question.company) && (
                      <BusinessIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
                    )}
                    <BusinessIcon sx={{ color: theme.palette.primary.main, mr: 1, display: 'none' }} />
                    <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                      {question.company}
                    </Typography>
                    <Chip 
                      label={question.difficulty} 
                      size="small"
                      sx={{ 
                        ml: 2,
                        ...getDifficultyColor(question.difficulty)
                      }}
                    />
                  </Box>
                  
                  {/* Question Type and Round */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WorkIcon sx={{ color: theme.palette.secondary.main, mr: 1, fontSize: '1rem' }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      {question.standardizedQuestionType} • {question.interviewType}
                    </Typography>
                  </Box>
                  
                  {/* Question Text */}
                  {isPremium ? (
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                      {question.interviewQuestion}
                    </Typography>
                  ) : (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        🔒 Premium content - Question details are hidden
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        You can still practice this question! Upgrade to Premium to see the question details beforehand.
                      </Typography>
                    </Box>
                  )}

                  {/* Additional Context */}
                  {isPremium && question.comments && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      💡 {question.comments}
                    </Typography>
                  )}
                  
                  {/* Footer */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Voting */}
                      <Tooltip title={!isSignedIn ? "Sign in to vote" : userVotes[question._id] ? "You have already voted" : "Vote on this question"}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVote(question._id, 'upvote');
                          }}
                          disabled={!isSignedIn || !!userVotes[question._id]}
                          sx={{
                            color: userVotes[question._id] === 'upvote' 
                              ? '#ff1744 !important' // Red color for upvote
                              : userVotes[question._id] 
                                ? `${theme.palette.grey[400]} !important`
                                : !isSignedIn
                                  ? `${theme.palette.grey[300]} !important`
                                  : 'inherit',
                            '&:hover': {
                              color: userVotes[question._id] === 'upvote' 
                                ? '#ff1744 !important'
                                : !isSignedIn
                                  ? `${theme.palette.grey[300]} !important`
                                  : 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <ThumbUpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {question.upvotes}
                      </Typography>
                      <Tooltip title={!isSignedIn ? "Sign in to vote" : userVotes[question._id] ? "You have already voted" : "Vote on this question"}>
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVote(question._id, 'downvote');
                          }}
                          disabled={!isSignedIn || !!userVotes[question._id]}
                          sx={{
                            color: userVotes[question._id] === 'downvote' 
                              ? '#1976d2 !important' // Blue color for downvote
                              : userVotes[question._id] 
                                ? `${theme.palette.grey[400]} !important`
                                : !isSignedIn
                                  ? `${theme.palette.grey[300]} !important`
                                  : 'inherit',
                            '&:hover': {
                              color: userVotes[question._id] === 'downvote' 
                                ? '#1976d2 !important'
                                : !isSignedIn
                                  ? `${theme.palette.grey[300]} !important`
                                  : 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <ThumbDownIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body2" sx={{ mr: 2 }}>
                        {question.downvotes}
                      </Typography>

                      {/* Tags */}
                      <Chip 
                        label={`${question.jobTitle}`} 
                        size="small" 
                        sx={{ 
                          backgroundColor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                          fontWeight: 'bold'
                        }} 
                      />
                    </Box>
                    
                    <Button 
                      size="small" 
                      variant={isPremium ? "contained" : "outlined"}
                      startIcon={<PlayIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePracticeQuestion(question);
                      }}
                      sx={isPremium ? { 
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        }
                      } : {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        }
                      }}
                    >
                      Practice
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No questions found matching your criteria.
              </Typography>
              <Button 
                variant="outlined" 
                onClick={resetFilters}
                sx={{ mt: 2 }}
              >
                Reset Filters
              </Button>
            </Paper>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}

          {/* Question Detail Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={() => setDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            {selectedQuestion && (
              <>
                <DialogTitle>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center">
                      {getCompanyLogo(selectedQuestion.company) ? (
                        <Box
                          component="img"
                          src={getCompanyLogo(selectedQuestion.company)}
                          alt={`${selectedQuestion.company} logo`}
                          sx={{
                            width: 28,
                            height: 28,
                            mr: 1.5,
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallbackIcon = e.target.nextSibling;
                            if (fallbackIcon) fallbackIcon.style.display = 'inline-flex';
                          }}
                        />
                      ) : (
                        <BusinessIcon sx={{ 
                          color: theme.palette.primary.main, 
                          mr: 1.5,
                          fontSize: 28
                        }} />
                      )}
                      <BusinessIcon sx={{ 
                        color: theme.palette.primary.main, 
                        mr: 1.5,
                        fontSize: 28,
                        display: 'none'
                      }} />
                      <Typography variant="h5" fontWeight="bold">
                        {selectedQuestion.company} Interview Question
                      </Typography>
                    </Box>
                    <Chip 
                      label={selectedQuestion.difficulty} 
                      sx={getDifficultyColor(selectedQuestion.difficulty)}
                    />
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <Box mb={3}>
                    {isPremium ? (
                      <Typography variant="h6" gutterBottom>
                        {selectedQuestion.interviewQuestion}
                      </Typography>
                    ) : (
                      <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1, mb: 2 }}>
                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                          🔒 Premium content - Question details are hidden
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          You can still practice this question! Upgrade to Premium to see the question details beforehand.
                        </Typography>
                      </Box>
                    )}
                    
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Type:</strong> {selectedQuestion.standardizedQuestionType}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Round:</strong> {selectedQuestion.interviewType}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Job Title:</strong> {selectedQuestion.jobTitle}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Experience Level:</strong> {selectedQuestion.experienceLevel}
                        </Typography>
                      </Grid>
                    </Grid>

                    {selectedQuestion.comments && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Additional Context:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedQuestion.comments}
                        </Typography>
                      </Box>
                    )}

                    {selectedQuestion.followUpQuestions?.length > 0 && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Follow-up Questions:
                        </Typography>
                        {selectedQuestion.followUpQuestions.map((followUp, index) => (
                          <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            • {followUp}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {selectedQuestion.tags?.length > 0 && (
                      <Box mt={3}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Tags:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {selectedQuestion.tags.map((tag, index) => (
                            <Chip key={index} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDialogOpen(false)}>
                    Close
                  </Button>
                  <Button 
                    variant={isPremium ? "contained" : "outlined"}
                    startIcon={<PlayIcon />}
                    onClick={() => {
                      setDialogOpen(false);
                      handlePracticeQuestion(selectedQuestion);
                    }}
                    sx={!isPremium ? {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main
                    } : {}}
                  >
                    Practice This Question
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </Box>
      </Container>
    </>
  );
};

export default EnhancedQuestionBank;